import React, { useState } from "react";
import {
  Card,
  CardContent,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Grid,
  Divider,
} from "@mui/material";
import { Description as DescriptionIcon } from "@mui/icons-material";
import httpRequest from "../utils/httpRequest";

const months = [
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
];

const CustomerRecoveryReportCard = ({ financialYears, onSnackbar }) => {
  const [selectedFY, setSelectedFY] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  const handleDownload = async () => {
    if (!selectedFY || !selectedMonth) {
      onSnackbar({
        open: true,
        message: "Please select financial year and month",
        severity: "warning",
      });
      return;
    }

    try {
      const params = {
        financial_year_id: selectedFY,
        month: selectedMonth,
      };

      const data = await httpRequest(
        "reporting/getCustomerRecoveryMonthly",
        "GET",
        null,
        params,
        "blob"
      );

      let filename = "customer_recovery_report.xlsx";
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();

      onSnackbar({
        open: true,
        message: "Customer Recovery Report downloaded successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error downloading customer recovery report:", error);
      onSnackbar({
        open: true,
        message: "Failed to download report",
        severity: "error",
      });
    }
  };

  return (
    <Card
      elevation={4}
      sx={{
        borderRadius: 3,
        bgcolor: "#f9f9f9",
        transition: "0.3s",
        "&:hover": { boxShadow: 6 },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <DescriptionIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Customer Recovery Monthly Report
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid size={{
              xs: 12,
              sm: 6
            }}>
            <FormControl fullWidth>
              <InputLabel id="fy-select-label">Financial Year</InputLabel>
              <Select
                labelId="fy-select-label"
                value={selectedFY}
                label="Financial Year"
                onChange={(e) => setSelectedFY(e.target.value)}
              >
                {financialYears.map((fy) => (
                  <MenuItem key={fy.id} value={fy.id}>
                    {fy.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <FormControl fullWidth>
              <InputLabel id="month-select-label">Month</InputLabel>
              <Select
                labelId="month-select-label"
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {months
                  .filter((month, index) => {
                    const selectedFYObj = financialYears.find(
                      (fy) => fy.id === selectedFY
                    );
                    if (!selectedFYObj) return true;

                    const now = new Date();
                    const selectedStart = new Date(selectedFYObj.start_date);
                    const selectedEnd = new Date(selectedFYObj.end_date);

                    const isCurrentFY =
                      now >= selectedStart && now <= selectedEnd;

                    if (!isCurrentFY) return true;

                    const fyStartYear = selectedStart.getFullYear();
                    const fyEndYear = selectedEnd.getFullYear();
                    
                    let monthDate;
                    if (index <= 8) {
                      const monthNumber = index + 3;
                      monthDate = new Date(fyStartYear, monthNumber, 1);
                    } else {
                      const monthNumber = index - 9;
                      monthDate = new Date(fyEndYear, monthNumber, 1);
                    }

                    return monthDate <= now;
                  })
                  .map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                onClick={handleDownload}
                disabled={!selectedFY || !selectedMonth}
                sx={{
                  minWidth: 200,
                  fontWeight: "bold",
                  textTransform: "none",
                }}
              >
                Download Recovery Report
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CustomerRecoveryReportCard;

